import type { ProductFormValues } from "./productForm";

/**
 * ═══════════════════════════════════════════════════════════════════
 * El producto, paso a paso. **Esta es la única declaración del reparto.**
 *
 * De aquí salen tres cosas que de otro modo se escribirían por separado
 * y acabarían contradiciéndose:
 *
 *  1. Los rótulos del `Stepper`.
 *  2. Qué validar al pulsar "Siguiente" — solo lo de ESTE paso, para que
 *     nadie vea en rojo un campo que todavía no ha visto.
 *  3. A qué paso pertenece un error que mandó el servidor, para poder
 *     llevar de vuelta a quien edita hasta él.
 *
 * Si el tercero se desincronizara del primero, un "ese slug ya existe"
 * señalado desde el paso 10 dejaría su mensaje en un paso oculto: un
 * botón de guardar que no hace nada y nadie sabe por qué.
 *
 * ── Los pasos son los GRUPOS del contrato ───────────────────────────
 * `discount`, `compensation`, `validity`, `shipping`, `datasheet` y
 * `editorial` están anidados en `product.ts` precisamente porque cada
 * uno es una decisión aparte. El reparto de abajo no inventa una
 * organización: la lee.
 * ═══════════════════════════════════════════════════════════════════
 */

/**
 * Un PREFIJO de campo. `"prices"` cubre `prices.MXN` y `prices.EUR`;
 * `"translations.es.name"` se cubre a sí mismo y nada más.
 *
 * Se guardan prefijos y no la lista completa porque media docena de
 * campos tienen llaves que no existen hasta que alguien las crea —las
 * monedas del mapa de precios, las filas de una lista— y una lista
 * cerrada se quedaría corta en cuanto se añadiera un mercado.
 */
export type ProductFieldPath = string;

export interface ProductStep {
  /** Identificador estable, para las pruebas y los saltos con nombre. */
  readonly key: string;
  /** Lo que se lee en el indicador de progreso. */
  readonly label: string;
  /** La frase que explica de qué va el paso. */
  readonly description: string;
  /** Qué se captura aquí. Prefijos: ver `ProductFieldPath`. */
  readonly fields: readonly ProductFieldPath[];
}

export const PRODUCT_STEPS: readonly ProductStep[] = [
  {
    key: "info",
    label: "Contenido",
    description: "Cómo se llama el producto y qué promete, en los dos idiomas.",
    /*
     * Sin `tagline`: el eslogan se mudó al paso de Promesas. Declararlo
     * en los dos sitios haría que `stepOfField` mandara un error del
     * servidor a ESTE paso, donde ya no hay control que pintarlo — un
     * "Guardar" que no hace nada y nadie sabe por qué.
     */
    fields: [
      "translations.es.name",
      "translations.es.description",
      "translations.es.disclaimer",
      "translations.en.name",
      "translations.en.description",
      "translations.en.disclaimer",
    ],
  },
  {
    key: "classification",
    label: "Clasificación",
    description: "Qué es, cuántas quedan y cómo se comporta en la tienda.",
    /*
     * ── El SLUG ya no está, y no es un descuido ─────────────────────
     * "En ningún momento te pedí este campo" (Anahí, 26-ago). Tiene
     * razón: la dirección pública de un producto no es una decisión que
     * quien da de alta tenga que tomar. Ahora se GENERA del nombre en
     * español —ver `productFormSchema`— y el servidor garantiza que sea
     * único poniéndole un sufijo si hace falta, porque desde que no hay
     * campo ya no hay dónde corregir un choque.
     *
     * Al EDITAR sí se enseña, de solo lectura, con `SlugField`: un slug
     * no se puede cambiar sin romper cada enlace ya compartido, pero
     * poder VERLO es otra cosa — es la dirección de la ficha.
     *
     * ── El recorte del 26-ago (segunda pasada) ──────────────────────
     * Anahí dictó qué lleva este paso y NADA MÁS: categoría, tipo de
     * ítem, lo que el tipo pida (composición de un paquete; descuento y
     * fechas de una promoción), stock y las opciones de activación.
     * Salieron `sortOrder`, `isWholesale`, `packageKind` (era un campo
     * fantasma sin control; retirado del todo el 2-oct-2026) y las fechas de vigencia de un producto
     * normal. Sus datos siguen viajando en el formulario —quitar el
     * control no borra lo guardado—, solo dejaron de editarse aquí.
     *
     * `discount` y `validity` se declaran AQUÍ porque este es el único
     * paso que los pinta: los enseña `ProductPromotionFields` cuando el
     * tipo es promoción. Para el resto de tipos van vacíos y validan en
     * silencio.
     */
    fields: [
      "category",
      "type",
      "activation",
      "stock",
      "items",
      "discount",
      "validity",
    ],
  },
  {
    key: "pricing",
    label: "Precios",
    description: "Lo que cuesta en cada país: público, socio y franquiciatario.",
    /*
     * ── Solo los tres precios de la lista de Anahí ──────────────────
     * "Precio público, precio socio y precio franquiciatario, solo
     * Estados Unidos y México" (26-ago). Público y socio se ESCRIBEN;
     * el de franquiciatario se DERIVA del porcentaje, que por eso sigue
     * declarado aquí — es el control del que sale ese precio.
     *
     * El precio tachado (`compareAtPrices`) y el descuento general
     * salieron de este paso: el descuento ahora solo existe para las
     * promociones y se edita en el paso 2, junto a sus fechas.
     *
     * `promoPrices` NO se declara: lo calcula el servidor desde
     * `discount`.
     */
    fields: [
      "currencies",
      "prices",
      "memberPrices",
      "franchiseeDiscountPercent",
    ],
  },
  {
    key: "points",
    label: "Puntos",
    description: "Los puntos MLM: PV y CV, la base del multinivel.",
    /*
     * `compensation` entero sigue declarado aunque solo se pinte el CV:
     * es el prefijo al que el servidor manda sus errores, y este es el
     * único paso que enseña algo suyo.
     */
    fields: ["volumePoints", "compensation"],
  },
  {
    key: "benefits",
    /*
     * "Beneficios", no "Promesas": es como Anahí nombra este paso en su
     * lista de siete. El ESLOGAN ya no se edita aquí (26-ago, segunda
     * pasada): su dato sigue viajando, pero sin control no se declara —
     * declarar un campo sin control mandaría un error del servidor a un
     * paso donde no hay nada que pintar.
     */
    label: "Beneficios",
    description: "Los beneficios del producto, en el orden en que se leen.",
    fields: ["benefits"],
  },
  {
    key: "extras",
    label: "Variantes y costos adicionales",
    description: "Colores, tallas, materiales y lo que cada uno suma al precio.",
    // El envío salió de AQUÍ el 26-ago (segunda pasada) y volvió el
    // 21-sep como paso propio: ver `shipping`, abajo.
    fields: ["extraCosts"],
  },
  {
    key: "shipping",
    label: "Envío",
    description: "Lo que pesa y mide ya empacado: con esto se pide la guía.",
    /*
     * ── El OCTAVO paso (Anahí, 21-sep-2026) ─────────────────────────
     * Con la llave de Trasso puesta, las guías se piden desde estos
     * cuatro números y tienen que poderse capturar. Anahí eligió un
     * paso nuevo —no meterlos en Clasificación ni devolverlos a
     * Variantes— para no tocar lo que dictó para los siete anteriores.
     * Los cuatro campos son opcionales: el hueco lo llena el estándar.
     */
    fields: ["shipping"],
  },
  {
    key: "media",
    label: "Imágenes y ficha técnica",
    description: "Las fotos del producto y lo que dice la etiqueta del bote.",
    /*
     * ── También estuvo partido, y también se deshizo ────────────────
     * "Fotos" y "Ficha técnica" fueron dos pasos porque juntos medían
     * 1207 px. Vuelven a ser uno: son el paso 7 de la lista de Anahí,
     * "Imágenes y ficha técnica".
     *
     * Presentación, posología y aviso legal viajan con el PDF: son la
     * ficha técnica del producto tanto como él, y quien los captura los
     * está copiando del bote que tiene en la mano.
     */
    fields: [
      "imageUrl",
      "imageUrls",
      "datasheet",
      "presentation",
      "dosage",
      "legend",
      "facts",
    ],
  },
];

export const PRODUCT_STEP_LABELS: readonly string[] = PRODUCT_STEPS.map(
  (step) => step.label,
);

/**
 * Índices con nombre, para que las pantallas y las pruebas no cuenten.
 *
 * ── SIETE pasos, que son los que Anahí pidió, MÁS Envío ─────────────
 * Los dictó dos veces y esta es su lista, palabra por palabra:
 *
 *   1 Contenido · 2 Clasificación · 3 Precios · 4 Puntos ·
 *   5 Beneficios · 6 Variantes y costos adicionales ·
 *   7 Imágenes y ficha técnica
 *
 * El 21-sep-2026 pidió que peso y medidas se capturaran —Trasso ya
 * tiene llave— y eligió un paso propio, "Envío", que va antes de las
 * imágenes: son OCHO, y los siete suyos siguen intactos.
 *
 * Hubo un rato en que fueron NUEVE: medidos a 1440×900, Precios pedía
 * 1353 px y la ficha 1207, así que se partieron en "Precios/Descuentos"
 * y "Fotos/Ficha técnica" para que cada mitad cupiera en una vista.
 *
 * Los cortes se DESHICIERON, y conviene saber a cambio de qué: los dos
 * pasos vuelven a pasarse de la pantalla. Está aceptado. Caber en una
 * vista es un objetivo del diseño; los siete pasos son el encargo, y
 * cuando los dos chocan gana el encargo. Lo que NO se hace para
 * disimularlo es partirlos otra vez, poner dos tarjetas en paralelo o
 * esconder campos detrás de un acordeón: un paso largo se recorre con la
 * rueda del ratón, un campo escondido no se rellena.
 *
 * Lo que sí sobrevive de aquel rato es la organización de dentro, que
 * era mejor y no costaba pasos: los porcentajes junto a los precios que
 * producen, y una sola tarjeta para la ficha del bote.
 *
 * ── Y qué pasó con los pasos que ya no están ────────────────────────
 *  · ENVÍO se metió en Variantes: peso y medidas son lo que decide qué
 *    cuesta mandar el producto.
 *  · VIGENCIA se metió en Clasificación, donde ya se enseñaba para las
 *    promociones.
 *  · NEGOCIO se repartió: el porcentaje de franquiciatario a Precios
 *    —porque de él sale un precio—, "es mayorista" a Clasificación, y la
 *    letra de la etiqueta a Imágenes y ficha técnica.
 *
 * Y el EDITORIAL salió del asistente a la pestaña "Detalles" del producto
 * ya creado, que es su propio asistente de cuatro pasos. Sus valores
 * siguen viajando en cada guardado de ESTE formulario —`productForm` los
 * carga y los envía—, así que editar un producto no le vacía la ficha
 * larga. Ver `productDetailSteps.ts`.
 */
export const STEP_INFO = 0;
export const STEP_CLASSIFICATION = 1;
export const STEP_PRICING = 2;
export const STEP_POINTS = 3;
export const STEP_BENEFITS = 4;
export const STEP_EXTRAS = 5;
export const STEP_SHIPPING = 6;
export const STEP_MEDIA = 7;

/** Los mapas de precio, que se expanden moneda a moneda. */
const PRICE_MAPS = [
  "prices",
  "memberPrices",
  "compareAtPrices",
] as const;

/**
 * Los campos CONCRETOS de un paso, listos para `form.trigger(...)`.
 *
 * Los prefijos de los mapas de precio se expanden con las monedas que el
 * formulario tiene AHORA (`prices.MXN`, `prices.EUR`…). Pasarle
 * `"prices"` a `trigger` a secas depende de cómo react-hook-form resuelva
 * una ruta que apunta a un objeto entero, y de eso depende que "Siguiente"
 * frene con un precio ilegible. Expandir no depende de nada: cada campo
 * que existe en pantalla se nombra.
 *
 * Un índice fuera de rango devuelve la lista vacía en vez de reventar:
 * "no hay nada que validar" es la respuesta honesta para un paso que no
 * existe.
 */
export function fieldsOfStep(
  step: number,
  values: Pick<ProductFormValues, "currencies" | "type">,
): string[] {
  const declared = [...(PRODUCT_STEPS[step]?.fields ?? [])];

  /*
   * `discount` y `validity` ya no son campos prestados: desde la
   * segunda pasada del 26-ago el ÚNICO paso que los pinta es el 2
   * (`ProductPromotionFields`, cuando el tipo es promoción), así que
   * están declarados ahí arriba. Para un producto normal van vacíos y
   * la validación pasa en silencio.
   */
  const codes = values.currencies
    .map((row) => row.code.trim().toUpperCase())
    .filter((code) => code !== "");

  return declared.flatMap((field) => {
    if ((PRICE_MAPS as readonly string[]).includes(field)) {
      // El prefijo también, para que un error puesto por el servidor
      // directamente en `prices` se pinte y frene el avance.
      return [field, ...codes.map((code) => `${field}.${code}`)];
    }
    return [field];
  });
}

/**
 * A qué paso pertenece un campo que señaló el SERVIDOR.
 *
 * El servidor habla en rutas del contrato (`prices.MXN`,
 * `editorial.es.pillars.items.0.title`), y esas rutas coinciden con los
 * nombres del formulario. Aquí se busca el paso cuyo prefijo las cubre.
 *
 * Devuelve `-1` cuando el campo no es de ningún paso: puede pasar el día
 * que el contrato crezca antes que este archivo, y es mejor quedarse
 * donde se está que saltar al paso 1 sin motivo.
 */
export function stepOfField(path: string): number {
  return PRODUCT_STEPS.findIndex((step) =>
    step.fields.some(
      (field) =>
        field === path || path.startsWith(`${field}.`) || path.startsWith(`${field}[`),
    ),
  );
}
